"use client";
import ShowcaseCard, { ShowcaseCardProps } from "../ShowcaseCard";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import { Children, isValidElement } from "react";
import MonarchResolver from "@/monarch/MonarchResolver";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { LearnPageRedesignTestResult } from "@/monarch/rules/learnPageRedesign/types";

interface LOBEntryPointsProps {
  header?: string;
  subheader?: string;
  showEntrypoints?: string;
  backgroundColor?: string;
  location?: string;
  children?: React.ReactNode;
}

export default function LOBEntryPoints({
  header,
  subheader,
  showEntrypoints = "true",
  backgroundColor = "white",
  location,
  children,
}: LOBEntryPointsProps = {}) {
  const shouldShowEntrypoints = showEntrypoints === "true";
  const finalBackgroundColor =
    backgroundColor === "putty" || backgroundColor === "white"
      ? backgroundColor
      : "white";

  const renderContent = () => {
    if (!shouldShowEntrypoints) {
      return null;
    }

    const backgroundColorMap = {
      putty: "bg-putty-100",
      white: "bg-white",
    };

    return (
      <div
        className={`flex flex-col justify-center items-center py-20 ${location === "learn-page" ? "mx-auto" : "mx-break-out"} ${backgroundColorMap[finalBackgroundColor]}`}
      >
        <div className="flex flex-col justify-center items-center mb-12 w-full">
          {header && (
            <div className="elm-d1 font-inter text-[44px] text-primary-500 font-black italic mb-6 text-center leading-[44px] tracking-[-0.06rem]">
              {header}
            </div>
          )}
          {subheader && (
            <div className="elm-p5 text-center text-primary-500 px-4">
              {subheader}
            </div>
          )}
        </div>
        {children && Children.count(children) > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {children}
          </div>
        )}
      </div>
    );
  };

  if (location === "learn-page") {
    return (
      <FeatureFlagComponentResolverClient
        flagKey="NEXT_PUBLIC_FEATURE_LEARN_PAGE_REDESIGN_TRAFFIC"
        variants={{
          legacy: null,
          test: (
            <MonarchResolver<LearnPageRedesignTestResult>
              experimentKey="useLearnPageRedesign"
              variants={{
                control: null,
                test: (
                  <MonarchDataConsumer<LearnPageRedesignTestResult>
                    experimentKey="useLearnPageRedesign"
                    render={(testResult) => {
                      if (
                        !testResult?.data?.lobEntryPoints ||
                        !testResult.data.lobEntryPoints.enabled
                      ) {
                        return null;
                      }
                      return (
                        <FeatureFlagComponentResolverClient
                          flagKey="NEXT_PUBLIC_FEATURE_LOB_ENTRY_POINTS"
                          variants={{
                            legacy: <></>,
                            test: renderContent(),
                          }}
                          fallback="legacy"
                        />
                      );
                    }}
                  />
                ),
              }}
              Loader={null}
              fallbackVariant="control"
            />
          ),
        }}
        fallback="legacy"
      />
    );
  }

  return (
    <FeatureFlagComponentResolverClient
      flagKey="NEXT_PUBLIC_FEATURE_LOB_ENTRY_POINTS"
      variants={{
        legacy: <></>,
        test: renderContent(),
      }}
      fallback="legacy"
    />
  );
}

export interface ShowcaseCardItemProps {
  header?: string;
  header_background_color?: "putty" | "fern" | "putty-light" | "fern-light";
  header_icon?: "book-open" | "business" | "certificate" | "school";
  hide_factoids_on_mobile?: boolean;
  card_subheader?: string;
  factoids?: string;
  cta_text?: string;
  cta_url?: string;
  children?: React.ReactNode[];
}

export function ShowcaseCardItem(props: ShowcaseCardItemProps) {
  let childrens = Children.toArray(props.children);
  let showcaseCard: ShowcaseCardProps = {
    header: props?.header || "",
    subheader: props?.card_subheader || "",
    headerBackgroundColor: props?.header_background_color || "putty",
    headerIcon: props?.header_icon || "certificate",
    factoids: [],
    cta: {
      text: props?.cta_text || "",
      url: props?.cta_url || "",
    },
    hideFactoidsOnMobile: props?.hide_factoids_on_mobile || false,
  };

  childrens.forEach((child) => {
    if (isValidElement(child) && child.props) {
      const factoidProps = child.props as FactoidsProps;
      if (factoidProps.text && factoidProps.icon) {
        showcaseCard.factoids.push({
          text: factoidProps.text,
          icon: factoidProps.icon,
        });
      }
    }
  });

  return (
    <div>
      {
        <ShowcaseCard
          key={showcaseCard.header}
          header={showcaseCard.header}
          subheader={showcaseCard.subheader}
          headerBackgroundColor={showcaseCard.headerBackgroundColor}
          headerIcon={showcaseCard.headerIcon}
          factoids={showcaseCard.factoids}
          cta={showcaseCard.cta}
          hideFactoidsOnMobile={showcaseCard.hideFactoidsOnMobile}
          location="lob-entrypoints"
          position={1}
        />
      }
    </div>
  );
}

export interface FactoidsProps {
  text?: string;
  icon?: "trending-up" | "certificate" | "timelapse";
  children?: React.ReactNode;
}

export function ShowcaseFactoidsItem({ children }: FactoidsProps) {
  return <div>{children}</div>;
}
